import { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import { MdAdd, MdDelete, MdEdit } from 'react-icons/md';

import Card from 'components/card';
import InputField from 'components/fields/InputField';
import TextField from 'components/fields/TextField';
import {
    getUnpaidEmailModels,
    createUnpaidEmailModel,
    updateUnpaidEmailModel,
    deleteUnpaidEmailModel,
    getInvoiceEmailModel,
} from 'interfaces/invoice';

const DEFAULT_TEMPLATES = [
    {
        _id: '__default1__',
        name: 'Relance amiable',
        content:
            'Bonjour,\n\nNous vous contactons au sujet de la facture {{numero-facture}} d\'un montant de {{montant-TTC}}€ TTC, dont le règlement ne nous est pas encore parvenu.\n\nPourriez-vous nous indiquer si un virement a déjà été effectué ou si vous rencontrez des difficultés ?\n\nCordialement,\nL\'équipe {{entreprise}}',
    },
    {
        _id: '__default2__',
        name: 'Relance formelle',
        content:
            'Bonjour,\n\nMalgré notre précédent rappel, la facture {{numero-facture}} d\'un montant de {{montant-TTC}}€ TTC reste impayée.\n\nNous vous demandons de bien vouloir régulariser cette situation dans les plus brefs délais.\n\nPassé ce délai, nous nous verrons dans l\'obligation d\'engager une procédure de recouvrement.\n\nCordialement,\nL\'équipe {{entreprise}}',
    },
];

const UnpaidEmailModal = (props: {
    isOpen: any;
    onClose: any;
    sendEmail: (name: string, content: string) => void;
    selectedInvoiceId?: string;
}) => {
    const { isOpen, onClose, sendEmail, selectedInvoiceId } = props;

    const [templates, setTemplates] = useState<any[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
    const [name, setName] = useState('');
    const [content, setContent] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [newContent, setNewContent] = useState('');
    const [isDirty, setIsDirty] = useState(false);

    const loadTemplates = async () => {
        const res = await getUnpaidEmailModels();
        const savedTemplates = res ?? [];
        const all = [...DEFAULT_TEMPLATES, ...savedTemplates];
        setTemplates(all);
        if (all.length > 0 && !selectedTemplate) {
            applyTemplate(all[0]);
        }
    };

    const loadCustomerEmail = async () => {
        if (!selectedInvoiceId) return;
        const res = await getInvoiceEmailModel(selectedInvoiceId);
        if (res) setCustomerEmail(res.customerEmail ?? '');
    };

    const applyTemplate = (tpl: any) => {
        setSelectedTemplate(tpl);
        setName(tpl.name);
        setContent(tpl.content);
        setIsDirty(false);
    };

    useEffect(() => {
        if (isOpen) {
            loadTemplates();
            loadCustomerEmail();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, selectedInvoiceId]);

    const handleSaveTemplate = async () => {
        if (!selectedTemplate || selectedTemplate._id.startsWith('__default')) return;
        await updateUnpaidEmailModel(selectedTemplate._id, name, content);
        setTemplates((prev) =>
            prev.map((t) =>
                t._id === selectedTemplate._id ? { ...t, name, content } : t,
            ),
        );
        setIsDirty(false);
    };

    const handleCreateTemplate = async () => {
        if (!newName || !newContent) return;
        const created = await createUnpaidEmailModel(newName, newContent);
        if (created) {
            const updated = [...templates, created];
            setTemplates(updated);
            applyTemplate(created);
        }
        setIsCreating(false);
        setNewName('');
        setNewContent('');
    };

    const handleDeleteTemplate = async (tpl: any) => {
        if (tpl._id.startsWith('__default')) return;
        await deleteUnpaidEmailModel(tpl._id);
        const updated = templates.filter((t) => t._id !== tpl._id);
        setTemplates(updated);
        if (updated.length > 0) applyTemplate(updated[0]);
        else {
            setSelectedTemplate(null);
            setName('');
            setContent('');
        }
    };

    const isDefaultTemplate = selectedTemplate?._id?.startsWith('__default');
    const canSend =
        selectedInvoiceId === 'all' ||
        (customerEmail && customerEmail.includes('@') && customerEmail.includes('.'));

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] !m-auto !w-max min-w-[350px] !max-w-[85%] md:top-[8vh]">
                <ModalBody>
                    <Card extra="px-[30px] pt-[35px] pb-[40px] max-w-[720px] flex flex-col !z-[1004]">
                        <h1 className="mb-1 text-2xl font-bold">
                            Envoyer par email
                        </h1>
                        <p className="mb-4 text-sm text-gray-500">
                            Destinataire :{' '}
                            <span className="font-medium text-navy-700 dark:text-white">
                                {customerEmail || 'inconnu'}
                            </span>
                        </p>

                        {/* Template selector */}
                        <div className="mb-4">
                            <p className="mb-1 text-sm font-bold text-navy-700 dark:text-white">
                                Modèle de relance
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {templates.map((tpl) => (
                                    <div
                                        key={tpl._id}
                                        className={`group flex items-center gap-1 rounded-full border px-3 py-1 text-sm cursor-pointer transition-colors ${
                                            selectedTemplate?._id === tpl._id
                                                ? 'border-toola-500 bg-toola-500 text-white'
                                                : 'border-gray-300 text-gray-600 hover:border-toola-400 hover:text-toola-500'
                                        }`}
                                        onClick={() => applyTemplate(tpl)}
                                    >
                                        {tpl.name}
                                        {!tpl._id.startsWith('__default') && (
                                            <MdDelete
                                                className="ml-1 h-3.5 w-3.5 opacity-60 hover:opacity-100"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteTemplate(tpl);
                                                }}
                                            />
                                        )}
                                    </div>
                                ))}
                                <div
                                    className="flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-3 py-1 text-sm cursor-pointer text-gray-400 hover:border-toola-400 hover:text-toola-500"
                                    onClick={() => setIsCreating(true)}
                                >
                                    <MdAdd className="h-3.5 w-3.5" />
                                    Nouveau
                                </div>
                            </div>
                        </div>

                        {/* New template form */}
                        {isCreating && (
                            <div className="mb-4 rounded-xl border border-dashed border-toola-300 bg-toola-50/30 p-4">
                                <p className="mb-2 text-sm font-bold text-navy-700">
                                    Créer un nouveau modèle
                                </p>
                                <InputField
                                    label="Nom du modèle"
                                    placeholder="Ex: Relance 2ème avertissement"
                                    id="new-template-name"
                                    value={newName}
                                    type="text"
                                    onChange={(e: any) => setNewName(e.target.value)}
                                />
                                <div className="mt-2">
                                    <TextField
                                        label="Contenu"
                                        id="new-template-content"
                                        placeholder="Votre message..."
                                        value={newContent}
                                        cols={30}
                                        rows={4}
                                        onChange={(e: any) =>
                                            setNewContent(e.target.value)
                                        }
                                    />
                                </div>
                                <p className="mt-1 text-xs text-gray-400">
                                    Variables disponibles : {'{{numero-facture}}'}, {'{{montant-TTC}}'}, {'{{entreprise}}'}
                                </p>
                                <div className="mt-3 flex gap-2">
                                    <button
                                        onClick={() => {
                                            setIsCreating(false);
                                            setNewName('');
                                            setNewContent('');
                                        }}
                                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        onClick={handleCreateTemplate}
                                        disabled={!newName || !newContent}
                                        className="rounded-lg bg-toola-500 px-4 py-2 text-sm font-semibold text-white hover:bg-toola-600 disabled:opacity-50"
                                    >
                                        Créer
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Edit area */}
                        {selectedTemplate && (
                            <>
                                <InputField
                                    label="Objet"
                                    placeholder="Objet de l'email"
                                    id="email-object"
                                    value={name}
                                    type="text"
                                    onChange={(e: any) => {
                                        setName(e.target.value);
                                        setIsDirty(true);
                                    }}
                                />
                                <div className="mt-2">
                                    <TextField
                                        label="Message"
                                        id="email-content"
                                        placeholder="Votre message"
                                        value={content}
                                        cols={30}
                                        rows={5}
                                        onChange={(e: any) => {
                                            setContent(e.target.value);
                                            setIsDirty(true);
                                        }}
                                    />
                                </div>
                                <p className="mt-1 text-xs text-gray-400">
                                    Variables disponibles : {'{{numero-facture}}'}, {'{{montant-TTC}}'}, {'{{entreprise}}'}
                                </p>
                                {isDirty && !isDefaultTemplate && (
                                    <button
                                        onClick={handleSaveTemplate}
                                        className="mt-2 flex items-center gap-1 text-xs text-toola-500 hover:underline"
                                    >
                                        <MdEdit className="h-3.5 w-3.5" />
                                        Enregistrer les modifications du modèle
                                    </button>
                                )}
                            </>
                        )}

                        {!canSend && selectedInvoiceId !== 'all' && (
                            <p className="mx-auto mt-3 text-sm text-red-500">
                                Impossible d'envoyer, aucun email renseigné pour ce client.
                            </p>
                        )}

                        <div className="mt-5 flex gap-2">
                            <button
                                onClick={onClose}
                                className="linear rounded-xl border-2 border-gray-700 px-5 py-3 text-base font-medium text-gray-700 transition duration-200 hover:bg-red-600/5 dark:border-red-400 dark:bg-red-400/10 dark:text-white"
                            >
                                Annuler
                            </button>
                            {canSend && (
                                <button
                                    className="linear ml-3 flex items-center justify-center rounded-xl bg-toola-500 px-5 py-3 text-base font-medium text-white hover:bg-toola-600 active:bg-toola-700"
                                    onClick={() => sendEmail(name, content)}
                                >
                                    Envoyer la relance
                                </button>
                            )}
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default UnpaidEmailModal;
